import { ApiError } from './index';

/**
 * 서버 에러를 **사용자에게 보여줄지 / 뭐라고 보여줄지** 판정하는 공용 규칙.
 *
 * ── 배경 ─────────────────────────────────────────────────────────────────
 * BE 가 BaseException 으로 한글 message 를 내려줘도, FE 에서 mutate() 를 그냥 호출하거나
 * `void mutateAsync()` 로 흘리면 아무 UI 반응 없이 콘솔에만 찍혔다. 사용자는 버튼을
 * 눌렀는데 아무 일도 안 일어난 것처럼 보인다 — 그게 가장 흔한 "무반응" 버그의 정체다.
 *
 * 여기서 규칙을 한 곳에 모으고, 각 앱의 QueryClient(MutationCache.onError)가 이 규칙으로
 * 전역 알림을 띄운다. 두 앱이 같은 판정을 쓰도록 api-client 에 둔다.
 */

/** 401 — 클라이언트가 refresh/로그인 리다이렉트로 이미 처리한다. */
const HTTP_UNAUTHORIZED = 401;
const HTTP_SERVER_ERROR = 500;

/**
 * BE 가 "진짜 서버 장애"로 내리는 code — 이때만 메시지 대신 일반 장애 안내를 띄운다.
 * 외부 연동 실패처럼 BE 가 사용자용 문구를 쓴 5xx 는 그 문구를 그대로 보여준다.
 * BE `SERVER_FAULT_ERROR_CODES`(platform-api libs/shared common.error-codes)와 짝이다.
 */
const SERVER_FAULT_CODES: ReadonlySet<string> = new Set([
  'COMMON_INTERNAL_ERROR',
  'COMMON_GATEWAY_MISCONFIGURED',
  'COMMON_UPSTREAM_UNAVAILABLE',
]);

/** 응답에 code 가 없을 때 normalizeError 가 채우는 값. */
const UNKNOWN_CODE = 'UNKNOWN';

const SERVER_FAULT_MESSAGE = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';

/** 본문 없이 ALB·Nginx 가 직접 거절한 4xx — axios 영문 문구 대신 쓴다. */
const BARE_STATUS_MESSAGES: Readonly<Record<number, string>> = {
  404: '요청한 주소를 찾을 수 없습니다.',
  413: '요청 크기가 너무 큽니다. 파일 크기를 줄여 다시 시도해주세요.',
  429: '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',
};

/** BE 가 `{ message }` 본문을 내렸는가. HTML 오류 페이지·빈 본문이면 false. */
function hasServerPayload(error: ApiError): boolean {
  const { detail } = error;
  return typeof detail === 'object' && detail !== null && 'message' in detail;
}

/**
 * 사용자가 고칠 수 없는 서버 장애인가 — 장애 code 이거나, code·본문 없는 5xx(크래시·프록시 오류).
 * BE 서비스 필터는 항상 code 를 싣는다 — code 없는 5xx 는 BE 가 쓴 문구가 아니다.
 */
export function isServerFault(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.kind !== 'SERVER') return false;
  if (error.status < HTTP_SERVER_ERROR) return false;
  return (
    SERVER_FAULT_CODES.has(error.code) ||
    error.code === UNKNOWN_CODE ||
    !hasServerPayload(error)
  );
}

/**
 * 이 에러를 사용자에게 알릴 것인가.
 *
 * 제외 대상:
 * - `CANCELED`      : 화면 전환/언마운트로 요청이 끊긴 것. 사용자 잘못이 아니고 알릴 일도 아니다.
 * - `401`           : onUnauthorized 가 로그인으로 보낸다. 알림까지 띄우면 리다이렉트 직전에
 *                     모달이 번쩍이고, 세션 만료마다 팝업이 쌓인다.
 */
export function shouldNotifyError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true; // 정체불명 예외일수록 알려야 한다
  if (error.kind === 'CANCELED') return false;
  if (error.kind === 'SERVER' && error.status === HTTP_UNAUTHORIZED) return false;
  return true;
}

/**
 * 사용자에게 보여줄 메시지.
 *
 * BE 의 message 를 **그대로** 쓰는 게 원칙이다. 서버가 상황에 맞는 한글 문구
 * ("배정 중인 좌석이 3석이라 …")를 이미 만들어 내려주는데, FE 가 "요청에 실패했습니다"
 * 같은 일반 문구로 덮으면 사용자가 무엇을 고쳐야 할지 알 수 없게 된다.
 *
 * 5xx 라도 BE 가 사용자용 문구를 실었으면 그대로 쓴다. 일반 장애 문구로 바꾸는 건
 * 네트워크·타임아웃과 {@link isServerFault} 인 경우뿐이다.
 */
export function toUserMessage(error: unknown, fallback = '요청 처리에 실패했습니다.'): string {
  if (error instanceof ApiError) {
    if (error.kind === 'NETWORK')
      return '네트워크 연결을 확인해주세요.';
    if (error.kind === 'TIMEOUT')
      return '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.';
    if (error.kind === 'SERVER') {
      if (isServerFault(error)) return SERVER_FAULT_MESSAGE;
      if (!hasServerPayload(error)) return BARE_STATUS_MESSAGES[error.status] ?? fallback;
    }
    if (error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** 알림 제목 — 종류별로 구분해 사용자가 "내 입력 문제"인지 "시스템 문제"인지 알게 한다. */
export function toUserTitle(error: unknown, fallback = '처리 실패'): string {
  if (error instanceof ApiError) {
    if (error.kind === 'NETWORK') return '네트워크 오류';
    if (error.kind === 'TIMEOUT') return '응답 지연';
    if (isServerFault(error)) return '서버 오류';
  }
  return fallback;
}
